import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Wallet,
  Users,
  GraduationCap,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SectionHeader from "@/components/site/SectionHeader";
import ModulesAccordion from "@/components/site/ModulesAccordion";
import PropractEmbed from "@/components/site/PropractEmbed";
import FAQList from "@/components/site/FAQList";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import ImportantNote from "@/components/site/ImportantNote";
import { BlobB, BlobC } from "@/components/site/BlobBackground";
import { FOUNDATION_TRACK, FAQS_FOUNDATION, SITE } from "@/lib/siteConfig";

const T = FOUNDATION_TRACK;

export default function CoursePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden hero-wash bg-grain">
        <BlobB className="absolute -top-24 -left-20 w-[420px] h-[420px] hidden sm:block" />
        <BlobC className="absolute -bottom-20 -right-20 w-[420px] h-[420px] hidden sm:block" />
        <div className="container-narrow relative py-16 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(200,106,74,0.12)] text-terracotta text-xs font-semibold tracking-wide">
                <Sparkles className="w-3.5 h-3.5" /> Foundation Track · Cohort 02
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink" data-testid="course-title">
                {T.fullName}
              </h1>
              <p className="mt-2 font-display italic text-lg sm:text-xl text-terracotta" data-testid="course-tagline">
                {T.tagline}
              </p>
              <p className="mt-2 text-sm text-ink-muted tracking-wide">
                {SITE.courseTagline} — <span className="italic">{SITE.pullQuote}</span>
              </p>
              <p className="mt-5 text-ink-muted text-base sm:text-lg leading-[1.75] max-w-2xl" data-testid="course-description">
                {T.description}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href="#enroll">
                  <Button data-testid="course-hero-cta-button" className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl px-6 py-6 text-base transition-colors">
                    Apply for Cohort 02
                  </Button>
                </a>
                <a href="#curriculum">
                  <Button variant="outline" className="rounded-xl px-6 py-6 text-base border-[rgba(74,63,57,0.18)] hover:bg-[rgba(200,106,74,0.08)] transition-colors">
                    See modules
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-paper rounded-3xl border border-[rgba(74,63,57,0.10)] shadow-[0_14px_40px_rgba(31,26,23,0.06)] p-6 sm:p-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">Quick facts</div>
                <h3 className="mt-1 font-display text-xl text-ink">Everything at a glance</h3>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <Fact icon={<Calendar className="w-4 h-4" />} label="Starts" value="1 Dec 2026" />
                  <Fact icon={<Calendar className="w-4 h-4" />} label="Ends" value="1 Jan 2027" />
                  <Fact icon={<Clock className="w-4 h-4" />} label="Duration" value={T.durationLabel} />
                  <Fact icon={<BookOpen className="w-4 h-4" />} label="Hours" value={T.totalHoursLabel} />
                  <Fact icon={<MapPin className="w-4 h-4" />} label="Format" value="Hybrid" />
                  <Fact icon={<MapPin className="w-4 h-4" />} label="Field" value="NCR" />
                  <Fact icon={<Wallet className="w-4 h-4" />} label="Fee" value={`INR ${T.feeINR.toLocaleString()}`} />
                  <Fact icon={<Users className="w-4 h-4" />} label="Selection" value="Interest-based" />
                </div>
                <p className="mt-4 text-xs text-ink-muted leading-snug">
                  {T.totalHoursNote}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="container-narrow py-16 sm:py-20">
        <SectionHeader eyebrow="Who it's for" title="Built for those starting out — curiosity is the only prerequisite." subtitle="You don't need a psychology background. You need attention, willingness, and an interest in human beings." testidPrefix="audience" />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {T.audience.map((a, i) => (
            <div key={i} data-testid={`audience-card-${i + 1}`} className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5 hover:shadow-[0_14px_40px_rgba(31,26,23,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow]">
              <div className="w-10 h-10 rounded-xl bg-[rgba(126,155,134,0.18)] text-moss flex items-center justify-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <p className="mt-4 text-ink leading-[1.6] text-[15px]">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MODULES */}
      <section id="curriculum" className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="Modules" title="Seven modules — taught, practised, and reflected on." subtitle="Tap any module to see what we cover. Real-world exposure begins only after foundational readiness is established." testidPrefix="modules" />
          <div className="mt-12">
            <ModulesAccordion modules={T.modules} accent="terracotta" testid="foundation-modules" />
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="container-narrow py-16 sm:py-20">
        <SectionHeader eyebrow="How we learn" title="Practice & learning methodology" subtitle="We learn by doing, reflecting, and trying again — not by accumulating slides." testidPrefix="methodology" />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {T.methodology.map((m, i) => (
            <div key={i} className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-moss shrink-0 mt-0.5" />
              <p className="text-ink leading-[1.6] text-[15px]">{m}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-ink-muted italic max-w-2xl">{T.selectionNote}</p>
      </section>

      {/* OUTCOMES */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="What you'll walk away with" title="Real skills, real evidence, real shifts." testidPrefix="outcomes" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {T.outcomes.map((o, i) => (
              <div key={i} data-testid={`outcome-card-${i + 1}`} className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-moss shrink-0 mt-0.5" />
                <p className="text-ink leading-[1.6] text-[15px]">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENROLL */}
      <section id="enroll" className="container-narrow py-16 sm:py-20">
        <SectionHeader eyebrow="Enroll" title="Apply for Cohort 02 — starts 1 December 2026." subtitle="Booking and payment are handled securely through Propract. Cohort sizes are intentionally small — we recommend applying early." testidPrefix="enroll" />
        <div className="mt-10">
          <PropractEmbed />
        </div>
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5">
          <div>
            <div className="font-display text-lg text-ink">Have questions before applying?</div>
            <p className="text-sm text-ink-muted mt-1">Reach out on WhatsApp or via our contact form — we'll happily get on a call.</p>
          </div>
          <Link to="/contact">
            <Button variant="outline" className="rounded-xl border-[rgba(74,63,57,0.18)] hover:bg-[rgba(200,106,74,0.08)] transition-colors" data-testid="enroll-contact-button">
              Talk to us first
            </Button>
          </Link>
        </div>
        <ImportantNote className="mt-6" />
      </section>

      {/* TESTIMONIALS */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="Voices" title="Reflections from alumni of our earlier cohorts." testidPrefix="course-testimonials" />
          <div className="mt-10">
            <TestimonialsSection />
          </div>
        </div>
      </section>

      {/* CROSS-PROMO */}
      <section className="container-narrow py-12">
        <div className="rounded-3xl bg-paper border border-[rgba(74,63,57,0.10)] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">Already a psychology postgrad?</div>
            <h3 className="mt-1 font-display text-xl sm:text-2xl text-ink">Take a look at the Advanced Practice Track.</h3>
            <p className="text-sm text-ink-muted mt-1">For PG students & early-career therapists — applied therapy modalities, ethics, supervised client work.</p>
          </div>
          <Link to="/practice-track">
            <Button data-testid="foundation-to-practice-link" className="bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl">
              View Advanced Practice <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-narrow py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="FAQs" title="Foundation Track — your questions, answered." subtitle="If you don't see your question here, message us on WhatsApp or via the contact page." testidPrefix="faq-foundation" />
          </div>
          <div className="lg:col-span-7">
            <FAQList items={FAQS_FOUNDATION} testidPrefix="faq-foundation" />
          </div>
        </div>
      </section>
    </>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-terracotta">{icon}</span>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">{label}</div>
        <div className="font-display text-[15px] text-ink leading-tight">{value}</div>
      </div>
    </div>
  );
}
